import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useStandardsList } from '../hooks/useStandardsList';
import { resolveExportedByDisplay } from '../lib/resolveExportedByDisplay';
import type { DevStandardsSummary } from '../types/devStandards';

interface VersionHistoryPanelProps {
  currentVersionId?: number | null;
  /** Called when user clicks "비교" on a card */
  onDiffRequest: (compareId: number) => void;
  /** The ID of the currently-active (is_active=true) document, so we know what to compare against */
  activeDocId?: number | null;
}

function formatVersionLabel(item: DevStandardsSummary, locale: string): string {
  const date = new Date(item.exportedAt);
  const dateStr = new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  return `${dateStr} (${item.docVersion})`;
}

export function VersionHistoryPanel({
  currentVersionId,
  onDiffRequest,
  activeDocId,
}: VersionHistoryPanelProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { data, isLoading } = useStandardsList(0, 10);

  const versions = data?.content ?? [];
  const hasOtherVersions = versions.length > 1 || (versions.length === 1 && versions[0].id !== currentVersionId);

  return (
    <div
      role="complementary"
      aria-label={t('devStandardsViewer.history.title')}
      style={{
        position: 'sticky',
        top: 80,
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto',
        borderLeft: '1px solid var(--color-surface-border)',
        minWidth: open ? 240 : 40,
        maxWidth: open ? 280 : 40,
        flexShrink: 0,
        transition: 'min-width 0.2s, max-width 0.2s',
        background: 'var(--color-surface-background)',
      }}
    >
      {/* Toggle button */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={t('devStandardsViewer.history.toggle')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          width: '100%',
          padding: 'var(--spacing-3) var(--spacing-2)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          fontFamily: 'var(--font-family-sans)',
          borderBottom: '1px solid var(--color-surface-border)',
        }}
      >
        <span
          style={{
            transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
            transition: 'transform 0.2s',
            display: 'inline-block',
            fontSize: 10,
          }}
        >
          &#9658;
        </span>
        {open && (
          <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden' }}>
            {t('devStandardsViewer.history.title')}
          </span>
        )}
      </button>

      {open && (
        <div style={{ padding: 'var(--spacing-2)' }}>
          {/* "현재(active) 보기" link — shown only when viewing a non-active version */}
          {searchParams.get('version') && (
            <button
              type="button"
              onClick={() => navigate('/standards')}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'center',
                padding: 'var(--spacing-1) var(--spacing-2)',
                marginBottom: 'var(--spacing-2)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-brand-primary)',
                background: 'transparent',
                border: '1px solid var(--color-brand-primary)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('devStandardsViewer.history.viewActive')}
            </button>
          )}

          {/* Loading skeleton */}
          {isLoading && (
            <div
              aria-busy="true"
              aria-label={t('devStandardsViewer.history.loading')}
              style={{
                background: 'var(--color-surface-muted)',
                borderRadius: 'var(--radius-md)',
                height: 120,
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          )}

          {/* No versions at all */}
          {!isLoading && versions.length === 0 && (
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                padding: 'var(--spacing-2)',
                textAlign: 'center',
              }}
            >
              {t('devStandardsViewer.history.empty')}
            </p>
          )}

          {/* Version cards */}
          {versions.map((item) => {
            const isCurrentlyViewed = currentVersionId === item.id;
            const isActive = item.active;
            const label = formatVersionLabel(item, i18n.language);
            const shortSha = item.sourceCommit ? item.sourceCommit.slice(0, 8) : null;
            const summaryFirstLine = item.summary?.split('\n')[0]?.slice(0, 60) ?? '';

            return (
              <div
                key={item.id}
                style={{
                  borderBottom: '1px solid var(--color-surface-border)',
                  marginBottom: 'var(--spacing-1)',
                  borderLeft: isCurrentlyViewed
                    ? '2px solid var(--color-brand-primary)'
                    : '2px solid transparent',
                  background: isCurrentlyViewed
                    ? 'var(--color-surface-muted)'
                    : 'transparent',
                  borderRadius: 'var(--radius-sm)',
                }}
              >
                {/* Version label button — click to switch view */}
                <button
                  type="button"
                  onClick={() => navigate(`/standards?version=${item.id}`)}
                  aria-current={isCurrentlyViewed ? 'true' : undefined}
                  aria-label={`${t('devStandardsViewer.history.viewVersion')} ${label}`}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    padding: 'var(--spacing-2) var(--spacing-2) var(--spacing-1)',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      color: 'var(--color-text-primary)',
                      fontFamily: 'var(--font-family-mono)',
                      marginBottom: 'var(--spacing-1)',
                    }}
                  >
                    {label}
                  </div>
                  {shortSha && (
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-muted)',
                        fontFamily: 'var(--font-family-mono)',
                        marginBottom: 'var(--spacing-1)',
                      }}
                    >
                      {/* #652: GitLab 커밋 딥링크 제거 — sha 평문(기존 폴백 경로로 통일) */}
                      {shortSha}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      marginBottom: 'var(--spacing-1)',
                    }}
                  >
                    {resolveExportedByDisplay(item.exportedBy, item.exportedByDisplay)}
                  </div>
                  {summaryFirstLine && (
                    <div
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                        marginBottom: 'var(--spacing-1)',
                      }}
                    >
                      {summaryFirstLine}
                    </div>
                  )}
                  {isActive && (
                    <span
                      style={{
                        display: 'inline-block',
                        fontSize: 'var(--font-size-xs)',
                        background: 'var(--color-success)',
                        color: 'var(--color-text-inverse)',
                        borderRadius: 'var(--radius-sm)',
                        padding: '0 var(--spacing-1)',
                        marginBottom: 'var(--spacing-1)',
                      }}
                    >
                      {t('devStandardsViewer.history.active')}
                    </span>
                  )}
                </button>

                {/* Compare button */}
                <div style={{ padding: '0 var(--spacing-2) var(--spacing-2)' }}>
                  {isActive && !isCurrentlyViewed ? (
                    /* The active version card — "현재 버전과 비교" only makes sense when
                       user is viewing a different (older) version */
                    hasOtherVersions ? (
                      <button
                        type="button"
                        onClick={() => onDiffRequest(item.id)}
                        style={compareButtonStyle}
                        aria-label={t('devStandardsViewer.history.compareWithActive')}
                        title={t('devStandardsViewer.history.compareWithActive')}
                      >
                        {t('devStandardsViewer.history.compareWithThis')}
                      </button>
                    ) : null
                  ) : (
                    /* Non-active version: "이 버전과 비교" (compares active vs this) */
                    activeDocId != null && item.id !== activeDocId ? (
                      <button
                        type="button"
                        onClick={() => onDiffRequest(item.id)}
                        style={compareButtonStyle}
                        aria-label={`${t('devStandardsViewer.history.compareWithThis')} ${label}`}
                        title={`${t('devStandardsViewer.history.compareWithThis')} ${label}`}
                      >
                        {t('devStandardsViewer.history.compare')}
                      </button>
                    ) : null
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const compareButtonStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: 'var(--spacing-1) var(--spacing-2)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  background: 'var(--color-surface-muted)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
  textAlign: 'left' as const,
};
